import { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send } from "lucide-react";
import { sendChat, createChatSession } from "@/lib/api";
import { BRAND } from "@/lib/brand";

const SESSION_KEY = "kc_session";

const loadSession = () => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.session_id && parsed.session_token) return parsed;
  } catch {}
  return null;
};

const saveSession = (s) => {
  try { localStorage.setItem(SESSION_KEY, JSON.stringify(s)); } catch {}
};

export const AIConcierge = () => {
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(loadSession);
  const [messages, setMessages] = useState([
    { role: "assistant", text: `Namaste 🙏 I am KC Divine Concierge. How may I guide your journey today — a stay, a Char Dham Yatra, or a Himalayan wedding?` }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const ensureSession = async () => {
    if (session) return session;
    const s = await createChatSession();
    saveSession(s);
    setSession(s);
    return s;
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMessages(m => [...m, { role: "user", text }]);
    setInput("");
    setLoading(true);
    try {
      const s = await ensureSession();
      const res = await sendChat(s.session_id, s.session_token, text);
      setMessages(m => [...m, { role: "assistant", text: res.reply }]);
    } catch {
      setMessages(m => [...m, { role: "assistant", text: `I don't have confirmed information at the moment. Please reach our team on WhatsApp ${BRAND.phone} or email ${BRAND.email}.` }]);
    } finally { setLoading(false); }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="concierge-open-btn"
          className="fixed right-4 md:right-5 bottom-4 md:bottom-5 z-40 flex items-center gap-2 md:gap-3 bg-[#0F0F0F] border border-[#D4AF37] text-[#F8F6F2] pl-3 md:pl-4 pr-3.5 md:pr-5 py-2.5 md:py-3 hover:bg-[#D4AF37] hover:text-[#0F0F0F] transition-colors group shadow-lg shadow-black/40"
          aria-label="Open KC Concierge Live Chat"
          title="KC Concierge · Live Chat"
        >
          <div className="w-6 h-6 md:w-7 md:h-7 border border-[#D4AF37] group-hover:border-[#0F0F0F] flex items-center justify-center">
            <Sparkles size={13} />
          </div>
          <div className="hidden sm:flex flex-col items-start leading-tight">
            <span className="text-[9px] uppercase tracking-[0.32em] text-[#D4AF37] group-hover:text-[#0F0F0F]/70">KC Concierge</span>
            <span className="text-[12px] uppercase tracking-[0.18em]">Live Chat</span>
          </div>
          <span className="sm:hidden text-[10px] uppercase tracking-[0.22em]">Live Chat</span>
        </button>
      )}

      {open && (
        <div
          className="fixed right-5 bottom-5 z-50 w-[92vw] sm:w-[420px] h-[600px] max-h-[85vh] concierge-glass flex flex-col overflow-hidden"
          data-testid="concierge-widget"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#D4AF37]/30">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 border border-[#D4AF37] flex items-center justify-center">
                <Sparkles size={15} className="text-[#D4AF37]" />
              </div>
              <div>
                <div className="font-serif-display text-[19px] leading-tight text-[#F8F6F2]">KC Concierge · Live Chat</div>
                <div className="text-[9px] uppercase tracking-[0.32em] text-[#D4AF37]">Nature · Comfort · Divinity</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-[#F8F6F2]/70 hover:text-[#D4AF37]" data-testid="concierge-close-btn" aria-label="Close">
              <X size={18} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-4" data-testid="concierge-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[86%] px-4 py-3 text-[13.5px] leading-relaxed ${
                  m.role === "user"
                    ? "bg-[#D4AF37] text-[#0F0F0F]"
                    : "bg-[#1A1A1B] text-[#F8F6F2] border border-[#D4AF37]/20"
                }`}>{m.text}</div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#1A1A1B] border border-[#D4AF37]/20 px-4 py-3 text-[12px] text-[#F8F6F2]/60 italic tracking-wide">Concierge is composing…</div>
              </div>
            )}
          </div>

          <div className="border-t border-[#D4AF37]/30 p-3 flex gap-2 bg-[#0F0F0F]/50">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") send(); }}
              placeholder="Ask about stays, Yatra, weddings…"
              className="flex-1 bg-transparent border border-[#D4AF37]/30 px-4 py-2.5 text-[13px] text-[#F8F6F2] placeholder:text-[#F8F6F2]/40 focus:outline-none focus:border-[#D4AF37]"
              data-testid="concierge-input"
              disabled={loading}
            />
            <button onClick={send} className="bg-[#D4AF37] hover:bg-[#F8F6F2] transition-colors text-[#0F0F0F] px-4 py-2.5" data-testid="concierge-send-btn" disabled={loading}>
              <Send size={15} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
